"use client";

import React, {
  useEffect,
  useCallback,
  useRef,
  useState,
  useMemo,
} from "react";
import { poolCapsForKeys } from "../../../lib/checkout/pool-availability";
import type { CustomerBookingHistory } from "../../../lib/checkout/types";
import { useCheckoutRegular } from "../hooks/use-checkout-regular";
import { CheckoutPanel } from "../checkout-panel";
import { CheckoutFooter } from "../checkout-footer";
import { CheckoutSummaryCard } from "../checkout-summary-card";
import { DateSelectionStep } from "../date-selection-step";
import { TicketSelectionStep } from "../ticket-selection-step";
import { TicketSelectionStepV2 } from "../ticket-selection-step-v2";
import { AddOnsStep } from "../add-ons-step";
import { UserFormStep } from "../user-form-step";
import type {
  UserFormStepHandle,
  UserFormNavState,
} from "../user-form-step";
import { ParticipantsStep, participantsToPayload } from "../participants-step";
import type {
  CheckoutParticipantField,
  ExistingParticipant,
} from "../participants-step";
import { BasketChoiceStep, type BasketChoiceNotice } from "../basket-choice-step";
import { ProgressBar } from "../progress-bar";
import { ToastProvider } from "../checkout-toast";
import { formatDate, formatTimeRange, scrollCheckoutContentToTop } from "../utils";
import { formatPolicyHint } from "../utils/format-policy-hint";
import { buildFallbackBookingPolicy } from "../utils/build-fallback-policy";
import { resolveSummaryLocationName } from "../utils/session-location";
import { buildQuestionPages } from "../utils/question-pages";
import { Button } from "../../button";
import { TriangleAlert, Loader2 } from "lucide-react";
import type { DiscountResult } from "../utils/types";

export type CheckoutRegularProps = {
  // Data
  course: any;
  sessions?: any[];
  ticketTypes: any[];
  ticketTypesV2?: any[];
  /** Equipment caps from the orchestrator's pool-availability read. */
  poolAvailability?: import("../../../lib/checkout/pool-availability").CheckoutPoolAvailability | null;
  priorPurchasedTicketIds?: string[];
  customerBookingHistory?: CustomerBookingHistory;
  cartPricingRules?: any[];
  addOns?: any[];
  addOnGroups?: any[];
  user?: any;
  bundleID?: string; // If using a purchased bundle, skip payment
  bundle?: any;
  locations?: any[]; // For location display
  tutors?: any[]; // For tutor info display
  /** Pre-select a session by ID (skips date selection step) */
  initialSessionId?: string;

  // Callbacks
  onLoadMoreDates: (args: {
    page: number;
    limit: number;
    sessionId?: string;
    fromDate?: string;
    locationId?: string;
  }) => Promise<any>;
  onConfirmFreeBooking?: (data: any) => Promise<void>;
  onValidateDiscount?: (code: string, email: string) => Promise<DiscountResult>;
  onSuccess?: (purchase: any) => void;
  onError?: (error: Error | string) => void;
  onClose?: () => void;
  onStepChange?: (step: string) => void; // Callback when step changes
  onJoinWaitlist?: (session?: any) => void; // Waiting list callback
  onViewBundlesClick?: () => void; // View bundles callback
  // Config
  currency?: string;
  timezone?: string;
  cancellationPolicyUrl?: string;
  /** Terms settings for dialog display */
  termsSettings?: import("../utils/types").TermsSettings;
  /** Custom terms for dialog display */
  customTerms?: import("../utils/types").CustomTerms;
  /** Callback for webcomponents to open terms dialog in external portal */
  onOpenTermsDialog?: (data: {
    termsSettings?: import("../utils/types").TermsSettings;
    customTerms?: import("../utils/types").CustomTerms;
    timezone?: string;
  }) => void;
  isSendingMaterial?: boolean;
  isBundleEnabled?: boolean; // Show bundle promo banner

  // School/Business settings
  school?: {
    name?: string;
    _id?: string;
    isNewsletterSignupEnabled?: boolean;
    customNewsletterSignupText?: string | null;
    businessCountry?: string;
  };
  allowPrefillOthersGuestsOnCheckout?: boolean;
  // Participants (attendees) — threaded from the orchestrator/widget.
  participantsEnabled?: boolean;
  participantLabel?: string;
  participantFields?: CheckoutParticipantField[];
  existingParticipants?: ExistingParticipant[];
  buyerIsAttendee?: boolean;
  /** Name-later listing: the attendee step accepts empty places. */
  allowUnnamedAttendees?: boolean;
  /** The signed-in customer's identity, for naming the buyer on their own card. */
  customer?: { firstName?: string; lastName?: string; email?: string; phone?: string };
  /** Answers the buyer has already given about themselves elsewhere in this basket. */
  selfSeed?: Record<string, unknown>;
  onSelfAnswersChange?: (answers: Record<string, unknown>) => void;
  /** When true, footer is not sticky - flows after content (for iframe embeds) */
  isEmbedMode?: boolean;
  /** When true, hides back navigation to options/dates (timetable Book Now) */
  isDirectBooking?: boolean;

  // Multi-date & Basket feature
  /** Whether multi-date selection is enabled (requires basket) */
  isMultiDateEnabled?: boolean;
  /** Maximum number of dates a customer can select (multi-date mode) */
  maxDateSelections?: number;
  /** When true, shows the bundle/membership helper (widget-only, basket-enabled). */
  showBundleMembershipHelp?: boolean;
  /** Current number of items in the basket */
  basketItemCount?: number;
  /**
   * Per-session quantity this customer has already added to their basket
   * (for this course). Subtracted from displayed "spots left" so the date
   * selector reflects what's actually addable for them.
   */
  reservedSessionQuantities?: Record<string, number>;
  /** Callback when user clicks "Add to Basket" */
  onAddToBasket?: (itemData: {
    type: "booking";
    courseId: string;
    courseName: string;
    sessionId: string;
    sessionDate: string;
    /** The LISTING's zone, so the basket dates this row on the same
     *  clock as an appointment row beside it rather than the browser's. */
    timezone?: string;
    ticketSelections: Array<{
      ticketTypeId: string;
      ticketTypeName: string;
      quantity: number;
      price: number;
      lineTotal?: number;
    }>;
    addOns?: Array<{
      addOnId: string;
      addOnName: string;
      quantity: number;
      price: number;
    }>;
    userInputs?: Array<{
      index: number;
      guestIndex: number | null;
      label: string;
      value: string | boolean | Date | null;
    }>;
    guestDetails?: {
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
    };
    checkoutGuestsInformation?: Array<{
      firstName: string;
      lastName: string;
      email: string;
      phoneNumber: string;
      marketingOptIn?: boolean;
      mandatoryTickBox?: boolean;
    }>;
    participants?: Array<{
      id?: string;
      answers: Array<{ slug: string; value: unknown }>;
    }>;
    price: number;
    currency: string;
    imageUrl?: string;
    isOnline?: boolean;
    /** Shared across the per-date items of one multi-date checkout pass so the
     * server can reconstruct the original order grouping (bundle rules, flat
     * price splitting). */
    checkoutGroupId?: string;
  }) => void;
  /** Callback when user clicks "Proceed to Checkout" in BasketChoiceStep — adds item to basket and navigates to basket checkout page */
  onProceedToBasketCheckout: (itemData: {
    type: "booking";
    courseId: string;
    courseName: string;
    sessionId?: string;
    sessionDate: string;
    /** The LISTING's zone, so the basket dates this row on the same
     *  clock as an appointment row beside it rather than the browser's. */
    timezone?: string;
    ticketSelections: Array<{
      ticketTypeId: string;
      ticketTypeName: string;
      quantity: number;
      price: number;
      lineTotal?: number;
    }>;
    addOns?: Array<{
      addOnId: string;
      addOnName: string;
      quantity: number;
      price: number;
    }>;
    userInputs?: Array<{
      index: number;
      guestIndex: number | null;
      label: string;
      value: string | boolean | Date | null;
    }>;
    guestDetails?: {
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
    };
    checkoutGuestsInformation?: Array<{
      firstName: string;
      lastName: string;
      email: string;
      phoneNumber: string;
      marketingOptIn?: boolean;
      mandatoryTickBox?: boolean;
    }>;
    participants?: Array<{
      id?: string;
      answers: Array<{ slug: string; value: unknown }>;
    }>;
    price: number;
    currency: string;
    imageUrl?: string;
    isOnline?: boolean;
    /** Shared across the per-date items of one multi-date checkout pass so the
     * server can reconstruct the original order grouping (bundle rules, flat
     * price splitting). */
    checkoutGroupId?: string;
  }) => void;
  /** Callback to trigger login flow. When provided and user is not logged in, shows "Log in" prompt in payment methods. */
  onLogin?: () => void;
  /**
   * When true, skip the basket choice step and auto-fire onProceedToBasketCheckout.
   */
  directCheckout?: boolean;
  /** Why the host refused the last basket choice; shown on the basket choice step. */
  basketConflict?: BasketChoiceNotice | null;
};

/**
 * Standalone checkout component for regular/drop-in courses
 * Flow: Dates -> Tickets -> AddOns -> UserForm -> Basket choice
 */
export function CheckoutRegular({
  course,
  sessions,
  ticketTypes,
  ticketTypesV2,
  priorPurchasedTicketIds,
  customerBookingHistory,
  cartPricingRules = [],
  addOns = [],
  addOnGroups = [],
  user,
  bundleID,
  bundle,
  locations = [],
  tutors = [],
  initialSessionId,
  onLoadMoreDates,
  onValidateDiscount,
  onClose,
  onStepChange,
  onJoinWaitlist,
  onViewBundlesClick,
  currency = "GBP",
  timezone,
  cancellationPolicyUrl = "/terms",
  termsSettings,
  customTerms,
  onOpenTermsDialog,
  isSendingMaterial = false,
  isBundleEnabled = false,
  school,
  allowPrefillOthersGuestsOnCheckout = false,
  participantsEnabled = false,
  participantLabel = "Attendees",
  participantFields = [],
  existingParticipants = [],
  buyerIsAttendee = false,
  allowUnnamedAttendees = false,
  customer,
  selfSeed,
  onSelfAnswersChange,
  isEmbedMode = false,
  isDirectBooking = false,
  isMultiDateEnabled = false,
  maxDateSelections = 10,
  basketItemCount = 0,
  reservedSessionQuantities,
  onAddToBasket,
  onProceedToBasketCheckout,
  directCheckout = false,
  basketConflict = null,
  showBundleMembershipHelp = false,
  poolAvailability = null,
}: CheckoutRegularProps) {
  // Footer-driven navigation for the booking-question form (paged forms —
  // legacy oneAtTime, or "all" + pageBreakBefore): the checkout footer's
  // Continue/Back drives the page pointer via this ref, and UserFormStep
  // reports nav state back here. Gated below on `isUserFormPaged`, not on
  // `userFormMode`, since a single-select "all" form can still page.
  const userFormRef = useRef<UserFormStepHandle>(null);
  const [userFormNav, setUserFormNav] = useState<UserFormNavState>({
    isFirst: true,
    isLast: false,
    canAdvance: false,
  });
  const userFormMode = course.userQuestionForm?.mode || "all";

  // Effective bundleID: prop-based (pre-selected)
  const effectiveBundleID = bundleID || undefined;

  const checkout = useCheckoutRegular({
    course,
    sessions,
    ticketTypes,
    ticketTypesV2,
    priorPurchasedTicketIds,
    customerBookingHistory,
    enabledAddOns: addOns,
    addOnGroups,
    locations,
    user,
    bundleID,
    bundle,
    isSendingMaterial,
    initialSessionId,
    reservedSessionQuantities,
    allowPrefillOthersGuestsOnCheckout,
    isMultiDateEnabled,
    maxDateSelections,
    participantsEnabled,
    hasParticipantFields: participantFields.length > 0,
    onLoadMoreDates,
    onValidateDiscount,
  });

  // Whether the booking-question form is actually paged (more than one page
  // once buildQuestionPages resolves `pageBreakBefore` / legacy `oneAtTime`).
  // Computed with the exact same inputs UserFormStep uses internally so the
  // two can never disagree — gating footer-driven nav on `userFormMode` alone
  // missed the "all" mode + page breaks case, which is this plan's whole point.
  const userFormPages = useMemo(() => {
    const sortedUserFormQuestions = [...(course.userQuestionForm?.questions || [])].sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0),
    );
    return buildQuestionPages(sortedUserFormQuestions, {
      mode: userFormMode,
      totalGuests: checkout.personalDetails.guests.length,
      collectSystemGuests: checkout.collectSystemGuests,
      participantsEnabled: checkout.showParticipantsStep,
    });
  }, [
    course.userQuestionForm?.questions,
    userFormMode,
    checkout.personalDetails.guests.length,
    checkout.collectSystemGuests,
    checkout.showParticipantsStep,
  ]);
  const isUserFormPaged = userFormPages.length > 1;

  // Equipment caps for the SELECTED window(s): single date → that session's
  // cap; multi-date → the MIN across selected dates (the all-or-nothing
  // charge gate refuses the whole selection if any date is short). undefined
  // = uncapped (no pool / failed fetch / outside range — fail open).
  const poolMaxTicketsByTicketTypeId = useMemo(
    () =>
      poolCapsForKeys(
        poolAvailability,
        isMultiDateEnabled && checkout.selectedSessions.length
          ? checkout.selectedSessions.map((s: any) => s.id)
          : checkout.selectedSession?.id
            ? [checkout.selectedSession.id]
            : [],
      ),
    [
      poolAvailability,
      isMultiDateEnabled,
      checkout.selectedSessions,
      checkout.selectedSession,
    ],
  );

  // Handle add to basket
  const handleAddToBasket = useCallback(() => {
    if (!onAddToBasket || !checkout.selectedSession) return;

    // Build ticket selections from checkout state
    const ticketSelections = checkout.ticketSelections
      .filter((sel) => sel.quantity > 0)
      .map((sel) => {
        const ticketType = checkout.ticketTypes.find(
          (tt: any) => tt.id === sel.ticketTypeId
        );
        const rawTicket = ticketTypesV2?.find((tt: any) => tt.id === sel.ticketTypeId);
        const v2Result = checkout.v2PricingResults?.find(
          (r: any) => r.ticketTypeId === sel.ticketTypeId,
        );
        const lineTotal = v2Result?.lineTotal;
        return {
          ticketTypeId: sel.ticketTypeId,
          ticketTypeName: ticketType?.name || "Ticket",
          quantity: sel.quantity,
          price: ticketType?.price || 0,
          lineTotal,
          numberOfGuestsPerTicket: ticketType?.numberOfGuestsPerTicket,
          typeCharge: ticketType?.typeCharge,
          bookingPolicy: rawTicket?.bookingPolicy ?? ticketType?.bookingPolicy ?? undefined,
        };
      });

    // Build add-ons from checkout state
    const addOnSelections = checkout.addOnSelections
      .filter((sel) => sel.quantity > 0)
      .map((sel) => {
        const allAddOns = [
          ...checkout.enabledAddOns,
          ...(checkout.addOnGroups || []).flatMap((g: any) => g.addOns),
        ];
        const addOn = allAddOns.find((a: any) => a.id === sel.addOnId);
        return {
          addOnId: sel.addOnId,
          addOnName: addOn?.name || "Add-on",
          quantity: sel.quantity,
          price: addOn?.price || 0,
          groupId: sel.groupId || null,
          groupName: sel.groupName || null,
        };
      });

    onAddToBasket({
      type: "booking",
      courseId: course._id || course.id,
      courseName: course.title,
      sessionId: checkout.selectedSession.id,
      sessionDate: checkout.selectedSession.startDateTime,
      timezone: timezone || course.timezone,
      ticketSelections,
      addOns: addOnSelections.length > 0 ? addOnSelections : undefined,
      userInputs: checkout.userFormValues?.length ? checkout.userFormValues : undefined,
      participants: checkout.showParticipantsStep
        ? participantsToPayload(checkout.participantsValue)
        : undefined,
      price: checkout.total,
      currency: currency,
      imageUrl: course.images?.[0]?.url,
      isOnline: checkout.selectedSession.isOnline ?? course.isOnline,
      guestDetails: {
        firstName: checkout.personalDetails.guests[0]?.firstName || "",
        lastName: checkout.personalDetails.guests[0]?.lastName || "",
        email: checkout.personalDetails.guests[0]?.email || "",
        phone: checkout.personalDetails.guests[0]?.phoneNumber || "",
      },
      checkoutGuestsInformation: checkout.personalDetails.guests.map((g) => ({
        firstName: g.firstName || "",
        lastName: g.lastName || "",
        email: g.email || "",
        phoneNumber: g.phoneNumber || "",
        marketingOptIn: g.marketingOptIn || false,
        mandatoryTickBox: g.mandatoryTickBox,
      })),
    });
  }, [
    timezone,
    onAddToBasket,
    checkout.selectedSession,
    checkout.ticketSelections,
    checkout.addOnSelections,
    checkout.ticketTypes,
    checkout.enabledAddOns,
    checkout.addOnGroups,
    checkout.total,
    checkout.v2PricingResults,
    checkout.personalDetails,
    checkout.userFormValues,
    // Without these the callback kept the attendee list from before the
    // participants step, so every booking sent `participants: []` (Oct 2026).
    checkout.showParticipantsStep,
    checkout.participantsValue,
    ticketTypesV2,
    course,
    currency,
  ]);

  const handleProceedToBasketCheckout = useCallback(() => {
    if (!checkout.selectedSession) return;

    const ticketSelections = checkout.ticketSelections
      .filter((sel) => sel.quantity > 0)
      .map((sel) => {
        const ticketType = checkout.ticketTypes.find(
          (tt: any) => tt.id === sel.ticketTypeId
        );
        const v2Result = checkout.v2PricingResults?.find(
          (r: any) => r.ticketTypeId === sel.ticketTypeId,
        );
        const lineTotal = v2Result?.lineTotal;
        return {
          ticketTypeId: sel.ticketTypeId,
          ticketTypeName: ticketType?.name || "Ticket",
          quantity: sel.quantity,
          price: ticketType?.price || 0,
          lineTotal,
          numberOfGuestsPerTicket: ticketType?.numberOfGuestsPerTicket,
          typeCharge: ticketType?.typeCharge,
        };
      });

    const addOnSelections = checkout.addOnSelections
      .filter((sel) => sel.quantity > 0)
      .map((sel) => {
        const allAddOns = [
          ...checkout.enabledAddOns,
          ...(checkout.addOnGroups || []).flatMap((g: any) => g.addOns),
        ];
        const addOn = allAddOns.find((a: any) => a.id === sel.addOnId);
        return {
          addOnId: sel.addOnId,
          addOnName: addOn?.name || "Add-on",
          quantity: sel.quantity,
          price: addOn?.price || 0,
          groupId: sel.groupId || null,
          groupName: sel.groupName || null,
        };
      });

    onProceedToBasketCheckout({
      type: "booking",
      courseId: course._id || course.id,
      courseName: course.title,
      sessionId: checkout.selectedSession.id,
      sessionDate: checkout.selectedSession.startDateTime,
      timezone: timezone || course.timezone,
      ticketSelections,
      addOns: addOnSelections.length > 0 ? addOnSelections : undefined,
      userInputs: checkout.userFormValues?.length ? checkout.userFormValues : undefined,
      participants: checkout.showParticipantsStep
        ? participantsToPayload(checkout.participantsValue)
        : undefined,
      price: checkout.total,
      currency: currency,
      imageUrl: course.images?.[0]?.url,
      isOnline: checkout.selectedSession.isOnline ?? course.isOnline,
      guestDetails: {
        firstName: checkout.personalDetails.guests[0]?.firstName || "",
        lastName: checkout.personalDetails.guests[0]?.lastName || "",
        email: checkout.personalDetails.guests[0]?.email || "",
        phone: checkout.personalDetails.guests[0]?.phoneNumber || "",
      },
      checkoutGuestsInformation: checkout.personalDetails.guests.map((g) => ({
        firstName: g.firstName || "",
        lastName: g.lastName || "",
        email: g.email || "",
        phoneNumber: g.phoneNumber || "",
        marketingOptIn: g.marketingOptIn || false,
        mandatoryTickBox: g.mandatoryTickBox,
      })),
    });
  }, [
    timezone,
    onProceedToBasketCheckout,
    checkout.selectedSession,
    checkout.ticketSelections,
    checkout.addOnSelections,
    checkout.ticketTypes,
    checkout.enabledAddOns,
    checkout.addOnGroups,
    checkout.total,
    checkout.v2PricingResults,
    checkout.personalDetails,
    checkout.userFormValues,
    // See handleAddToBasket: the direct-checkout auto-proceed fires this, so a
    // stale attendee list here dropped every embed booking's attendees.
    checkout.showParticipantsStep,
    checkout.participantsValue,
    course,
    currency,
  ]);

  // One id per multi-date pass: every per-date item of the pass carries it so
  // the server can re-group them into the original order (session-bundle
  // rules, flat price splitting).
  const newCheckoutGroupId = () =>
    `cg_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;

  // Multi-date: add all selected sessions to basket then proceed to basket checkout
  const handleMultiDateAddToBasket = useCallback(() => {
    if (!onAddToBasket || checkout.selectedSessions.length === 0) return;

    // Per-session ticket selections: each session-item carries the
    // rule-adjusted lineTotal for that specific date (e.g. £50 for the Friday
    // override, £72.50 for Thursday). Falls back to the cross-date average
    // when the hook didn't supply a breakdown (single-session paths).
    const sessionCount = checkout.selectedSessions.length;
    const buildTicketSelsForSession = (sessionIndex: number) =>
      checkout.ticketSelections
        .filter((sel) => sel.quantity > 0)
        .map((sel) => {
          const ticketType = checkout.ticketTypes.find(
            (tt: any) => tt.id === sel.ticketTypeId,
          );
          const rawTicket = ticketTypesV2?.find((tt: any) => tt.id === sel.ticketTypeId);
          const v2Result = checkout.v2PricingResults?.find(
            (r: any) => r.ticketTypeId === sel.ticketTypeId,
          );
          const perSessionLineTotal = v2Result?.perSessionLineTotals?.[sessionIndex];
          const lineTotal = perSessionLineTotal
            ?? (v2Result?.lineTotal !== undefined && sessionCount > 0
              ? Math.round(v2Result.lineTotal / sessionCount)
              : undefined);
          return {
            ticketTypeId: sel.ticketTypeId,
            ticketTypeName: ticketType?.name || "Ticket",
            quantity: sel.quantity,
            price: ticketType?.price || 0,
            lineTotal,
            numberOfGuestsPerTicket: ticketType?.numberOfGuestsPerTicket,
            typeCharge: ticketType?.typeCharge,
            bookingPolicy: rawTicket?.bookingPolicy ?? ticketType?.bookingPolicy ?? undefined,
          };
        });

    const allAddOns = [
      ...checkout.enabledAddOns,
      ...(checkout.addOnGroups || []).flatMap((g: any) => g.addOns),
    ];
    const addOnSels = checkout.addOnSelections
      .filter((sel) => sel.quantity > 0)
      .map((sel) => {
        const addOn = allAddOns.find(
          (a: any) => a.id === sel.addOnId,
        );
        return {
          addOnId: sel.addOnId,
          addOnName: addOn?.name || "Add-on",
          quantity: sel.quantity,
          price: addOn?.price || 0,
          groupId: sel.groupId || null,
          groupName: sel.groupName || null,
        };
      });

    const guestDetails = {
      firstName: checkout.personalDetails.guests[0]?.firstName || "",
      lastName: checkout.personalDetails.guests[0]?.lastName || "",
      email: checkout.personalDetails.guests[0]?.email || "",
      phone: checkout.personalDetails.guests[0]?.phoneNumber || "",
    };

    const checkoutGuestsInformation = checkout.personalDetails.guests.map((g) => ({
      firstName: g.firstName || "",
      lastName: g.lastName || "",
      email: g.email || "",
      phoneNumber: g.phoneNumber || "",
      marketingOptIn: g.marketingOptIn || false,
      mandatoryTickBox: g.mandatoryTickBox,
    }));

    const userInputs = checkout.userFormValues.length > 0 ? checkout.userFormValues : undefined;
    const participants = checkout.showParticipantsStep
      ? participantsToPayload(checkout.participantsValue)
      : undefined;

    // Per-session item price = sum of per-session ticket lineTotals for that
    // session, plus add-ons. Backend revalidates per-session anyway, but having
    // the correct figure up front means the basket panel sums correctly and the
    // confirmation email reads accurate amounts.
    const addOnsItemTotal = addOnSels.reduce(
      (sum, a) => sum + (a.price ?? 0) * (a.quantity ?? 0),
      0,
    );
    const fallbackPerSession = Math.round(checkout.total / Math.max(1, sessionCount));
    const checkoutGroupId = newCheckoutGroupId();

    for (let i = 0; i < checkout.selectedSessions.length; i++) {
      const session = checkout.selectedSessions[i]!;
      const ticketSelsForSession = buildTicketSelsForSession(i);
      const sessionTicketsTotal = ticketSelsForSession.reduce(
        (sum, ts) =>
          sum + (ts.lineTotal ?? (ts.price ?? 0) * (ts.quantity ?? 0)),
        0,
      );
      const sessionPrice = sessionTicketsTotal > 0
        ? sessionTicketsTotal + addOnsItemTotal
        : fallbackPerSession;

      onAddToBasket({
        type: "booking",
        courseId: course._id || course.id,
        courseName: course.title,
        sessionId: session.id,
        sessionDate: session.startDateTime,
        timezone: timezone || course.timezone,
        ticketSelections: ticketSelsForSession,
        addOns: addOnSels.length > 0 ? addOnSels : undefined,
        userInputs,
        participants,
        price: sessionPrice,
        currency,
        imageUrl: course.images?.[0]?.url,
        isOnline: session.isOnline ?? course.isOnline,
        guestDetails,
        checkoutGuestsInformation,
        checkoutGroupId,
      });
    }
  }, [
    timezone,
    onAddToBasket,
    checkout.selectedSessions,
    checkout.ticketSelections,
    checkout.addOnSelections,
    checkout.ticketTypes,
    checkout.enabledAddOns,
    checkout.addOnGroups,
    checkout.total,
    checkout.personalDetails,
    checkout.userFormValues,
    checkout.v2PricingResults,
    checkout.showParticipantsStep,
    checkout.participantsValue,
    course,
    currency,
    ticketTypesV2,
  ]);

  // Multi-date: add all selected sessions to basket then proceed to basket checkout
  const handleMultiDateProceedToCheckout = useCallback(() => {
    if (checkout.selectedSessions.length === 0) return;
    if (!onAddToBasket) return;

    // Per-session ticket selections — see handleMultiDateAddToBasket above.
    const sessionsLength = checkout.selectedSessions.length;
    const buildTicketSelsForSession = (sessionIndex: number) =>
      checkout.ticketSelections
        .filter((sel) => sel.quantity > 0)
        .map((sel) => {
          const ticketType = checkout.ticketTypes.find(
            (tt: any) => tt.id === sel.ticketTypeId,
          );
          const rawTicket = ticketTypesV2?.find((tt: any) => tt.id === sel.ticketTypeId);
          const v2Result = checkout.v2PricingResults?.find(
            (r: any) => r.ticketTypeId === sel.ticketTypeId,
          );
          const perSessionLineTotal = v2Result?.perSessionLineTotals?.[sessionIndex];
          const lineTotal = perSessionLineTotal
            ?? (v2Result?.lineTotal !== undefined && sessionsLength > 0
              ? Math.round(v2Result.lineTotal / sessionsLength)
              : undefined);
          return {
            ticketTypeId: sel.ticketTypeId,
            ticketTypeName: ticketType?.name || "Ticket",
            quantity: sel.quantity,
            price: ticketType?.price || 0,
            lineTotal,
            bookingPolicy: rawTicket?.bookingPolicy ?? ticketType?.bookingPolicy ?? undefined,
          };
        });

    const allAddOns = [
      ...checkout.enabledAddOns,
      ...(checkout.addOnGroups || []).flatMap((g: any) => g.addOns),
    ];
    const addOnSels = checkout.addOnSelections
      .filter((sel) => sel.quantity > 0)
      .map((sel) => {
        const addOn = allAddOns.find(
          (a: any) => a.id === sel.addOnId,
        );
        return {
          addOnId: sel.addOnId,
          addOnName: addOn?.name || "Add-on",
          quantity: sel.quantity,
          price: addOn?.price || 0,
          groupId: sel.groupId || null,
          groupName: sel.groupName || null,
        };
      });

    const guestDetails = {
      firstName: checkout.personalDetails.guests[0]?.firstName || "",
      lastName: checkout.personalDetails.guests[0]?.lastName || "",
      email: checkout.personalDetails.guests[0]?.email || "",
      phone: checkout.personalDetails.guests[0]?.phoneNumber || "",
    };

    const checkoutGuestsInformation = checkout.personalDetails.guests.map((g) => ({
      firstName: g.firstName || "",
      lastName: g.lastName || "",
      email: g.email || "",
      phoneNumber: g.phoneNumber || "",
      marketingOptIn: g.marketingOptIn || false,
      mandatoryTickBox: g.mandatoryTickBox,
    }));

    const userInputs = checkout.userFormValues.length > 0 ? checkout.userFormValues : undefined;
    const participants = checkout.showParticipantsStep
      ? participantsToPayload(checkout.participantsValue)
      : undefined;
    const sessions = checkout.selectedSessions;

    const addOnsItemTotal = addOnSels.reduce(
      (sum, a) => sum + (a.price ?? 0) * (a.quantity ?? 0),
      0,
    );
    const fallbackPerSession = Math.round(
      checkout.total / Math.max(1, sessions.length),
    );

    const buildSessionItem = (sessionIndex: number) => {
      const ticketSelsForSession = buildTicketSelsForSession(sessionIndex);
      const sessionTicketsTotal = ticketSelsForSession.reduce(
        (sum, ts) =>
          sum + (ts.lineTotal ?? (ts.price ?? 0) * (ts.quantity ?? 0)),
        0,
      );
      const sessionPrice = sessionTicketsTotal > 0
        ? sessionTicketsTotal + addOnsItemTotal
        : fallbackPerSession;
      return { ticketSelsForSession, sessionPrice };
    };

    const checkoutGroupId = newCheckoutGroupId();

    // Add all sessions except the last via onAddToBasket
    for (let i = 0; i < sessions.length - 1; i++) {
      const session = sessions[i]!;
      const { ticketSelsForSession, sessionPrice } = buildSessionItem(i);
      onAddToBasket({
        type: "booking",
        courseId: course._id || course.id,
        courseName: course.title,
        sessionId: session.id,
        sessionDate: session.startDateTime,
        timezone: timezone || course.timezone,
        ticketSelections: ticketSelsForSession,
        addOns: addOnSels.length > 0 ? addOnSels : undefined,
        userInputs,
        participants,
        price: sessionPrice,
        currency,
        imageUrl: course.images?.[0]?.url,
        isOnline: session.isOnline ?? course.isOnline,
        guestDetails,
        checkoutGuestsInformation,
        checkoutGroupId,
      });
    }

    // Last session: add to basket + navigate to checkout
    const lastSession = sessions[sessions.length - 1]!;
    const { ticketSelsForSession: lastTicketSels, sessionPrice: lastSessionPrice } =
      buildSessionItem(sessions.length - 1);
    onProceedToBasketCheckout({
      type: "booking",
      courseId: course._id || course.id,
      courseName: course.title,
      sessionId: lastSession.id,
      sessionDate: lastSession.startDateTime,
      timezone: timezone || course.timezone,
      ticketSelections: lastTicketSels,
      addOns: addOnSels.length > 0 ? addOnSels : undefined,
      userInputs,
      participants,
      price: lastSessionPrice,
      currency,
      imageUrl: course.images?.[0]?.url,
      isOnline: lastSession.isOnline ?? course.isOnline,
      guestDetails,
      checkoutGuestsInformation,
      checkoutGroupId,
    });
  }, [
    timezone,
    onAddToBasket,
    onProceedToBasketCheckout,
    checkout.selectedSessions,
    checkout.ticketSelections,
    checkout.addOnSelections,
    checkout.ticketTypes,
    checkout.enabledAddOns,
    checkout.addOnGroups,
    checkout.total,
    checkout.personalDetails,
    checkout.userFormValues,
    checkout.v2PricingResults,
    checkout.showParticipantsStep,
    checkout.participantsValue,
    ticketTypesV2,
    course,
    currency,
  ]);

  // Fallback booking policy from school-level settings (for tickets without their own policy)
  const fallbackBookingPolicy = useMemo(
    () => buildFallbackBookingPolicy(termsSettings),
    [termsSettings],
  );

  // Handle per-ticket policy click via external portal (webcomponents)
  const handlePolicyClick = useMemo(() => {
    if (!onOpenTermsDialog) return undefined;
    return (ticketName: string, policy: any) => {
      onOpenTermsDialog({
        ticketPolicies: [{ ticketName, policy }],
      });
    };
  }, [onOpenTermsDialog]);

  // Distinct locations of this listing's upcoming sessions (from the course
  // summary) resolved against the school's location list. 2+ entries makes
  // the date step render its location filter dropdown.
  const locationOptions = useMemo(() => {
    const ids: string[] = course?.summary?.locationIds ?? [];
    return ids
      .map((id) => {
        const loc = locations.find((l: any) => l._id === id || l.id === id);
        return loc ? { id, name: loc.name as string } : null;
      })
      .filter((o): o is { id: string; name: string } => o !== null && !!o.name);
  }, [course?.summary?.locationIds, locations]);

  // Step labels for breadcrumbs and progress bar
  const stepLabels: Record<string, string> = {
    dates: "Date selection",
    tickets: "Tickets",
    addons: "Add-ons",
    participants: participantLabel,
    userform: "Customer Information",
    personal: "Personal details",
    basketChoice: "Basket",
    payment: "Complete Booking",
  };

  // Progress bar steps
  const progressSteps = checkout.visibleSteps.map((step: string) => ({
    id: step,
    label: stepLabels[step] || step,
    shortLabel:
      {
        dates: "Date",
        tickets: "Tickets",
        addons: "Add-ons",
        participants: participantLabel,
        userform: "Info",
        personal: "Details",
        basketChoice: "Basket",
        payment: "Payment",
      }[step] || step,
  }));

  // Call onStepChange when step changes and scroll to top
  useEffect(() => {
    onStepChange?.(checkout.step);
    // Reset footer nav state when not on the userform step, so re-entering it
    // (e.g. Back from payment) doesn't act on a stale isLast from the previous
    // visit — which would make a fast Continue skip the form to the next step.
    // UserFormStep remounts on re-entry and re-reports its real nav state.
    if (checkout.step !== "userform") {
      setUserFormNav({ isFirst: true, isLast: false, canAdvance: false });
    }
    // Scroll the checkout's own scroll container to top on step change. Shadow-DOM
    // aware and never scrolls the host window (would yank an embedded page to top).
    setTimeout(() => scrollCheckoutContentToTop(), 50);
  }, [checkout.step, onStepChange]);

  // Format selected date for display
  const formattedDate = (() => {
    // Multi-date: show count of selected dates
    if (isMultiDateEnabled && checkout.selectedSessions.length > 0) {
      const count = checkout.selectedSessions.length;
      return `${count} ${count === 1 ? "date" : "dates"} selected`;
    }

    if (!checkout.selectedSession) return undefined;

    const session = checkout.selectedSession;
    const tz = timezone || course.timezone;
    // Use session's isOnline if available, fall back to course's isOnline
    const isOnline = session.isOnline ?? course.isOnline ?? false;

    // For online sessions, use user's local timezone; for offline, use course timezone
    const displayTimezone = isOnline && typeof window !== "undefined"
      ? Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || tz
      : tz;

    // For terms, use the term name as the primary label
    if (session.termName) {
      if (
        session.startDateTime &&
        !isNaN(new Date(session.startDateTime).getTime())
      ) {
        return `${session.termName}, ${formatDate(session.startDateTime, displayTimezone)} at ${formatTimeRange(session.startDateTime, session.duration, tz, isOnline)}`;
      }
      return session.termName;
    }

    // For regular sessions, format the date/time
    if (
      session.startDateTime &&
      !isNaN(new Date(session.startDateTime).getTime())
    ) {
      return `${formatDate(session.startDateTime, displayTimezone)} at ${formatTimeRange(session.startDateTime, session.duration, tz, isOnline)}`;
    }

    return undefined;
  })();

  // Venue for the sidebar summary and the review step (rationale lives on
  // resolveSummaryLocationName). Plain call — it's a cheap array lookup.
  const summaryLocationLabel = resolveSummaryLocationName(
    course,
    locations,
    checkout.selectedSession,
  );

  // Build ticket items for summary. In multi-date checkouts where different
  // sessions resolve to different prices/rules (e.g. Thursday £72.50 + Friday
  // £50 with a Weekend Premium override), emit one summary row per unique
  // (finalPrice, rules) so each row shows the correct per-date price and the
  // Weekend Premium savings badge reflects only the dates that actually matched.
  type SummaryTicketItem = {
    name: string;
    price: number;
    fullPrice?: number;
    lineTotal?: number;
    count: number;
    ticketTypeGroup?: string;
    isDeposit?: boolean;
    depositNowTotal?: number;
    depositContractTotal?: number;
    remainderCollection?: "payment_link" | "in_person" | null;
    balanceDueDate?: string | null;
    instalmentPreview?: Array<{
      label: string | null;
      amountLinePence?: number;
      amountUnitPence: number;
      dueType: string;
      dueDate: string | null;
      collection: "payment_link" | "in_person";
      offlineNote: string | null;
    }> | null;
    instalmentDiscountShares?: number[] | null;
    numberOfGuestsPerTicket: number;
    policyHint?: string;
    bookingPolicy?: any;
    appliedRules?: { ruleName: string; adjustment: number }[];
    originalUnitPrice?: number;
    sessionsMultiplier?: number;
  };

  const ticketItems: SummaryTicketItem[] = checkout.ticketSelections.flatMap(
    (sel): SummaryTicketItem[] => {
      const ticketType = checkout.ticketTypes.find(
        (tt: any) => tt.id === sel.ticketTypeId,
      );
      if (!ticketType || sel.quantity === 0) return [];

      const rawTicket = ticketTypesV2?.find((tt: any) => tt.id === sel.ticketTypeId);
      const v2Result = checkout.v2PricingResults?.find(
        (r: any) => r.ticketTypeId === sel.ticketTypeId,
      );

      const displayPrice = ticketType.effectivePrice ?? ticketType.price;
      // A V2 `chargeType:"deposit"` ticket carries its deposit split on the
      // pricing result (per-unit figures pre-scaled by the hook), not the legacy
      // `ticketTypeGroup:"deposit"` marker.
      const isV2Deposit =
        v2Result?.chargeType === "deposit" && v2Result?.depositAmount != null;
      const isDeposit = ticketType.ticketTypeGroup === "deposit" || isV2Deposit;
      const depositPrice = isV2Deposit
        ? (v2Result.depositUnit ?? displayPrice)
        : isDeposit
          ? (ticketType.depositAmount ?? displayPrice)
          : displayPrice;
      const fullPrice = isV2Deposit ? (v2Result.fullUnit ?? displayPrice) : displayPrice;
      // Flat-per-order / flat-per-session short-circuit the rule engine in the
      // hook — `v2Result.lineTotal` is the authoritative figure (basePrice for
      // flat-per-order, basePrice × sessions for flat-per-session). Quantity
      // does not multiply, so recomputing lineTotal as `qty × finalPrice` here
      // would over-state. Stick with the hook's value for these.
      const isFlatLineSemantic = !!v2Result?.isFlatPerOrder;

      const baseItem: Omit<SummaryTicketItem, "lineTotal" | "appliedRules" | "sessionsMultiplier"> = {
        name: ticketType.name,
        price: depositPrice,
        fullPrice: isDeposit ? fullPrice : undefined,
        count: sel.quantity,
        ticketTypeGroup: ticketType.ticketTypeGroup,
        isDeposit,
        depositNowTotal: isV2Deposit ? (v2Result?.depositLinePence ?? undefined) : undefined,
        depositContractTotal: isV2Deposit
          ? (v2Result?.depositContractPence ?? undefined)
          : undefined,
        remainderCollection: isV2Deposit ? v2Result?.remainderCollection : undefined,
        balanceDueDate: isV2Deposit ? v2Result?.balanceDueDate : undefined,
        instalmentPreview: isV2Deposit ? v2Result?.instalmentPreview : undefined,
        instalmentDiscountShares: isV2Deposit
          ? (checkout.v2InstalmentDiscountShares?.get(sel.ticketTypeId) ?? null)
          : undefined,
        numberOfGuestsPerTicket: ticketType.numberOfGuestsPerTicket ?? 1,
        policyHint: formatPolicyHint(rawTicket?.bookingPolicy),
        bookingPolicy: rawTicket?.bookingPolicy ?? ticketType.bookingPolicy ?? null,
        originalUnitPrice: v2Result?.result.basePrice,
      };

      const defaultMultiplier =
        checkout.isMultiDateEnabled && checkout.dateCount > 1
          ? checkout.dateCount
          : 1;

      // Group sessions by (finalPrice, applied-rules signature) to find unique
      // rate groups. Falls through to the single-row path when only one group
      // exists (single-date, uniform multi-date, or no V2 result).
      const perSessionResults = !isDeposit ? v2Result?.perSessionResults : undefined;
      const groupsMap = new Map<
        string,
        {
          finalPrice: number;
          appliedRules: Array<{ ruleId?: string; ruleName: string; adjustment: number }>;
          sessions: number;
        }
      >();
      if (perSessionResults && perSessionResults.length > 0) {
        for (const s of perSessionResults) {
          const ruleSig = (s.appliedRules ?? [])
            .map((r: any) => r.ruleId ?? r.ruleName)
            .sort()
            .join(",");
          const key = `${s.finalPrice}|${ruleSig}`;
          const existing = groupsMap.get(key);
          if (existing) {
            existing.sessions += 1;
          } else {
            groupsMap.set(key, {
              finalPrice: s.finalPrice,
              appliedRules: s.appliedRules ?? [],
              sessions: 1,
            });
          }
        }
      }

      if (groupsMap.size > 1) {
        // Mixed rates — emit one row per rate group. `lineTotal` is the
        // group's full contribution (sessions × qty × finalPrice); the
        // summary card divides by `sessionsMultiplier` for per-date display
        // and FinalStep sums them to get the order subtotal.
        return Array.from(groupsMap.values()).map((g) => ({
          ...baseItem,
          lineTotal: g.sessions * sel.quantity * g.finalPrice,
          appliedRules: g.appliedRules.map((r: any) => ({
            ruleName: r.ruleName,
            adjustment: r.adjustment,
          })),
          sessionsMultiplier: g.sessions,
        }));
      }

      // Uniform path — one row using the representative `result`.
      const v2AppliedRules = v2Result?.result.appliedRules?.map((r: any) => ({
        ruleName: r.ruleName,
        adjustment: r.adjustment,
      }));
      // `lineTotal` is the row's full contribution to the order total:
      //   normal multi-date     = dateCount × qty × per-session-finalPrice
      //   normal single-date    = qty × per-session-finalPrice
      //   flat-per-order/session = hook's lineTotal (already aggregate, qty doesn't multiply)
      //   deposit                = undefined (summary falls back to item.price)
      // The summary card divides by `sessionsMultiplier` to render the per-date
      // value; FinalStep sums lineTotals to get the order subtotal.
      const uniformPerSessionPrice = !isDeposit
        ? (v2Result?.result.finalPrice ?? depositPrice)
        : depositPrice;
      const uniformSessionsMultiplier = isFlatLineSemantic ? 1 : defaultMultiplier;
      const uniformLineTotal = isDeposit
        ? undefined
        : isFlatLineSemantic
          ? v2Result?.lineTotal
          : defaultMultiplier * sel.quantity * uniformPerSessionPrice;

      return [
        {
          ...baseItem,
          lineTotal: uniformLineTotal,
          appliedRules: v2AppliedRules,
          sessionsMultiplier: uniformSessionsMultiplier,
        },
      ];
    },
  );

  // When ticket selections resolved to mixed per-date rates, the summary card
  // and footer's "N × £perDate" line becomes a misleading average — each ticket
  // row already shows its own rate. Hide the redundant line in that case.
  const hasMixedRates = checkout.v2PricingResults?.some(
    (r: any) => {
      const psr = r?.perSessionResults;
      if (!psr || psr.length < 2) return false;
      const firstKey = `${psr[0].finalPrice}|${(psr[0].appliedRules ?? [])
        .map((rr: any) => rr.ruleId ?? rr.ruleName)
        .sort()
        .join(",")}`;
      return psr.some((s: any) => {
        const key = `${s.finalPrice}|${(s.appliedRules ?? [])
          .map((rr: any) => rr.ruleId ?? rr.ruleName)
          .sort()
          .join(",")}`;
        return key !== firstKey;
      });
    },
  ) ?? false;

  // Build add-on items for summary
  const addOnItems = checkout.addOnSelections
    .map((sel) => {
      const allAddOns = [
        ...checkout.enabledAddOns,
        ...(checkout.addOnGroups || []).flatMap((g: any) => g.addOns),
      ];
      const addOn = allAddOns.find((a: any) => a.id === sel.addOnId);
      if (!addOn || sel.quantity === 0) return null;
      return {
        name: addOn.name,
        price: addOn.price,
        count: sel.quantity,
        groupId: sel.groupId || null,
        groupName: sel.groupName || null,
      };
    })
    .filter(Boolean) as { name: string; price: number; count: number; groupId: string | null; groupName: string | null }[];

  // Build ticket policies for terms dialog
  const ticketPolicies = checkout.ticketSelections
    .map((sel) => {
      const rawTicket = ticketTypesV2?.find((tt: any) => tt.id === sel.ticketTypeId);
      if (!rawTicket?.bookingPolicy || sel.quantity === 0) return null;
      return {
        ticketName: rawTicket.name,
        policy: rawTicket.bookingPolicy,
      };
    })
    .filter(Boolean) as Array<{ ticketName: string; policy: any }>;

  return (
    <ToastProvider>
      <CheckoutPanel>
        <CheckoutPanel.Header
          title={course.title}
          subtitle="Regular Booking"
          onClose={isDirectBooking ? undefined : onClose}
        />

        <CheckoutPanel.Content isEmbedMode={isEmbedMode}>
          <CheckoutPanel.Main>
            {/* Progress Bar */}
            <ProgressBar
              steps={progressSteps}
              currentStep={checkout.currentStepIndex}
              onStepClick={(index) => {
                // In direct booking mode, prevent going back to dates step
                if (isDirectBooking && checkout.visibleSteps[index] === "dates") {
                  return;
                }
                checkout.goToStep(checkout.visibleSteps[index]!);
              }}
              className="mb-6"
            />

            {/* Date Selection Step */}
            {checkout.step === "dates" && (
              <DateSelectionStep
                sessions={checkout.sessions}
                selectedSessionId={checkout.selectedSession?.id}
                onSelectSession={(session) =>
                  checkout.selectSession(session.id)
                }
                hasMoreDates={!checkout.isFullyLoaded}
                onLoadMore={checkout.loadMoreDates}
                isLoading={checkout.isLoading}
                currency={currency}
                timezone={timezone || course.timezone}
                tutors={tutors.map((t: any) => ({
                  id: t._id || t.id,
                  name: t.name,
                  fullName: t.fullName,
                  imageUrl: t.profile?.imageUrl || t.imageUrl,
                }))}
                onJoinWaitlist={onJoinWaitlist}
                schoolId={school?._id}
                hidePrice={!!effectiveBundleID}
                isFromPrice={course.priceDisplayMode === "manual" && course.isPriceFrom === true}
                hideDisplayAvailableTickets={course?.displaySettings?.hideDisplaysAvailableTickets ?? false}
                isMultiSelect={isMultiDateEnabled}
                selectedSessionIds={checkout.selectedSessions.map((s) => s.id)}
                onToggleSession={(session) => checkout.toggleSession(session.id)}
                maxDateSelections={checkout.maxDateSelections}
                fromDate={checkout.fromDate}
                onChangeFromDate={checkout.setDateFilter}
                hideSoldOut={checkout.hideSoldOut}
                onChangeHideSoldOut={checkout.setHideSoldOut}
                locationOptions={locationOptions}
                locationFilter={checkout.locationFilter}
                onChangeLocationFilter={checkout.setLocationFilter}
                reservedSessionQuantities={reservedSessionQuantities}
              />
            )}

            {/* Ticket Selection Step */}
            {checkout.step === "tickets" && !checkout.selectedSession && (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            )}
            {checkout.step === "tickets" && checkout.selectedSession && (
              <>
                {isMultiDateEnabled && checkout.multiDateAvailability?.hasRestriction && (
                  <div className="flex items-start gap-2.5 p-3 mb-4 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                    <TriangleAlert className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                    <div className="text-amber-800 space-y-1.5">
                      <p>
                        You can only book for up to{" "}
                        <span className="font-semibold">
                          {checkout.multiDateAvailability.minAvailability}
                        </span>{" "}
                        guests, as this is the maximum capacity left for{" "}
                        <span className="font-semibold">
                          {formatDate(
                            checkout.multiDateAvailability.restrictiveSessions[0]?.startDateTime ?? "",
                            timezone || course.timezone,
                          )}
                        </span>
                        .
                      </p>
                      <p className="text-amber-700 text-xs">
                        Please select the number of people attending. This{" "}
                        <span className="font-semibold">quantity will automatically apply to all your selected dates</span>
                        . To book for a larger group, go back and deselect this date.
                      </p>
                    </div>
                  </div>
                )}
                {checkout.isV2 ? (
                  <TicketSelectionStepV2
                    ticketTypes={checkout.ticketTypes}
                    selections={checkout.ticketSelections}
                    onSelectionChange={checkout.updateTicketSelection}
                    currency={currency}
                    totalAvailability={
                      isMultiDateEnabled && checkout.multiDateAvailability
                        ? checkout.multiDateAvailability.minAvailability
                        : checkout.selectedSession?.availability
                    }
                    hidePrice={!!effectiveBundleID}
                    bundleMaxTickets={effectiveBundleID && bundle ? (bundle.totalSessions - (bundle.usedSessions || 0)) : undefined}
                    isMultiDateEnabled={isMultiDateEnabled}
                    dateCount={checkout.dateCount}
                    constraintErrors={checkout.constraintErrors}
                    cartPricingRules={cartPricingRules}
                    poolMaxTicketsByTicketTypeId={poolMaxTicketsByTicketTypeId}
                    fallbackBookingPolicy={fallbackBookingPolicy}
                    onPolicyClick={handlePolicyClick}
                    showBundleMembershipHelp={showBundleMembershipHelp}
                  />
                ) : (
                  <TicketSelectionStep
                    ticketTypes={checkout.ticketTypes}
                    selections={checkout.ticketSelections}
                    onSelectionChange={checkout.updateTicketSelection}
                    currency={currency}
                    totalAvailability={
                      isMultiDateEnabled && checkout.multiDateAvailability
                        ? checkout.multiDateAvailability.minAvailability
                        : checkout.selectedSession?.availability
                    }
                    hidePrice={!!effectiveBundleID}
                    bundleMaxTickets={effectiveBundleID && bundle ? (bundle.totalSessions - (bundle.usedSessions || 0)) : undefined}
                    isMultiDateEnabled={isMultiDateEnabled}
                    dateCount={checkout.dateCount}
                    fallbackBookingPolicy={fallbackBookingPolicy}
                    onPolicyClick={handlePolicyClick}
                    showBundleMembershipHelp={showBundleMembershipHelp}
                  />
                )}

                {checkout.hasMixedPolicies && (
                  <div className="flex items-start gap-2.5 p-3 mt-4 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                    <TriangleAlert className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                    <p className="text-amber-800">
                      The selected tickets have different cancellation policies and cannot be combined in the same booking. Please select tickets that share the same policy.
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Add-ons Step */}
            {checkout.step === "addons" && (
              <AddOnsStep
                addOns={checkout.enabledAddOns}
                groups={checkout.addOnGroups}
                selections={checkout.addOnSelections}
                onSelectionChange={checkout.updateAddOnSelection}
                currency={currency}
              />
            )}

            {/* User Form Step */}
            {checkout.step === "participants" && (
              <ParticipantsStep
                sessionDate={checkout.selectedSession?.startDateTime}
                participantLabel={participantLabel}
                fields={participantFields}
                existing={existingParticipants}
                maxParticipants={Math.max(1, checkout.totalGuestCount)}
                ticketGroups={checkout.ticketSeatGroups}
                value={checkout.participantsValue}
                onChange={checkout.setParticipantsValue}
                onValidationChange={checkout.setIsParticipantsValid}
                buyerIsAttendee={buyerIsAttendee}
                allowUnnamed={allowUnnamedAttendees}
                buyerSeededRef={checkout.buyerAttendeeSeededRef}
                selfSeed={selfSeed}
                onSelfAnswersChange={onSelfAnswersChange}
                buyer={{
                  // The typed personal-details step wins; the signed-in
                  // customer's own record fills in when this flow skips it
                  // (basket-enabled listings collect the buyer downstream).
                  firstName: checkout.personalDetails.guests[0]?.firstName || customer?.firstName,
                  lastName: checkout.personalDetails.guests[0]?.lastName || customer?.lastName,
                  email: checkout.personalDetails.guests[0]?.email || customer?.email,
                  phone: checkout.personalDetails.guests[0]?.phoneNumber || customer?.phone,
                }}
              />
            )}

            {checkout.step === "userform" && (
              <UserFormStep
                ref={userFormRef}
                questions={course.userQuestionForm?.questions || []}
                totalGuests={checkout.personalDetails.guests.length}
                collectSystemGuests={checkout.collectSystemGuests}
                participantsEnabled={checkout.showParticipantsStep}
                values={checkout.userFormValues}
                onChange={checkout.setUserFormValues}
                onValidationChange={checkout.setIsUserFormValid}
                onNavStateChange={setUserFormNav}
                footerDrivenNav={isUserFormPaged}
                mode={userFormMode}
              />
            )}


            {/* Basket Choice Step */}
            {checkout.step === "basketChoice" && (
              <BasketChoiceStep
                itemName={course.title}
                dateLabel={formattedDate}
                total={isMultiDateEnabled ? checkout.multiDateTotal : checkout.total}
                currency={currency}
                basketItemCount={basketItemCount}
                onAddToBasket={isMultiDateEnabled ? handleMultiDateAddToBasket : handleAddToBasket}
                onProceedToCheckout={
                  isMultiDateEnabled
                    ? handleMultiDateProceedToCheckout
                    : handleProceedToBasketCheckout
                }
                directCheckout={directCheckout}
                basketConflict={basketConflict}
              />
            )}
          </CheckoutPanel.Main>

          <CheckoutPanel.Sidebar>
            <CheckoutSummaryCard
              title={course.title}
              // A selected date's own photo wins over the listing cover. Multi-date
              // keeps the cover: several dates can carry several different photos.
              thumbnail={
                (!isMultiDateEnabled && checkout.selectedSession?.image?.url) ||
                course.images?.[0]?.url
              }
              description={formattedDate}
              locationLabel={summaryLocationLabel}
              isOnline={course.isOnline}
              availability={checkout.availability}
              hideDisplayAvailableTickets={course?.displaySettings?.hideDisplaysAvailableTickets ?? false}
              items={[...ticketItems, ...addOnItems]}
              total={checkout.total}
              totalRemainingBalance={checkout.totalRemainingBalance}
              currency={currency}
              dateCount={checkout.dateCount}
              multiDateTotal={checkout.multiDateTotal}
              hidePerDateBreakdown={hasMixedRates}
              discount={checkout.discountAmount}
              discountPercentage={checkout.discount?.type === "PERCENTAGE" ? checkout.discount?.value : undefined}
              onApplyDiscount={checkout.applyDiscount}
              cancellationPolicyUrl={cancellationPolicyUrl}
              termsSettings={termsSettings}
              customTerms={customTerms}
              timezone={timezone}
              onOpenTermsDialog={onOpenTermsDialog}
              onPolicyClick={handlePolicyClick}
              ticketPolicies={ticketPolicies.length > 0 ? ticketPolicies : undefined}
              showBundleMembershipHelp={showBundleMembershipHelp}
              bundleInfo={
                effectiveBundleID
                  ? {
                      ticketCount: ticketItems.reduce(
                        (sum, item) => sum + item.count * item.numberOfGuestsPerTicket,
                        0,
                      ),
                    }
                  : undefined
              }
            />

            {/* Bundle banner - only show if bundles are enabled */}
            {checkout.step === "dates" &&
              isBundleEnabled &&
              onViewBundlesClick && (
                <div className="mt-4 p-4 bg-blue-100 dark:bg-blue-900 rounded-lg border border-blue-500">
                  <h4 className="font-semibold text-blue-700 dark:text-blue-300">
                    Bundle tickets available
                  </h4>
                  <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                    Book multiple classes at once for a bigger saving!
                  </p>
                  <Button
                    variant="link"
                    className="text-blue-700 dark:text-blue-300 p-0 h-auto mt-2"
                    onClick={onViewBundlesClick}
                  >
                    View bundles
                  </Button>
                </div>
              )}
          </CheckoutPanel.Sidebar>
        </CheckoutPanel.Content>
      </CheckoutPanel>

      {/* Footer - hide continue button on basketChoice step (it has its own action buttons) */}
      <CheckoutFooter
        label="Date Selected"
        value={formattedDate}
        total={checkout.total}
        currency={currency}
        dateCount={checkout.dateCount}
        multiDateTotal={checkout.multiDateTotal}
        hidePerDateBreakdown={hasMixedRates}
        showBack={!checkout.isFirstStep && !(isDirectBooking && checkout.step === "tickets")}
        onBack={() => {
          // Paged booking form: Back walks the page pointer until the first
          // page, then leaves the step.
          if (
            checkout.step === "userform" &&
            isUserFormPaged &&
            !userFormNav.isFirst
          ) {
            userFormRef.current?.goPrevious();
            return;
          }
          checkout.goToPreviousStep();
        }}
        showCancel={!!onClose}
        onCancel={onClose}
        showContinue={checkout.step !== "basketChoice"}
        onContinue={() => {
          // Paged booking form: Continue advances the page pointer until the
          // end view, then leaves the step.
          if (
            checkout.step === "userform" &&
            isUserFormPaged &&
            !userFormNav.isLast
          ) {
            userFormRef.current?.goNext();
            return;
          }
          checkout.goToNextStep();
        }}
        continueText="Continue"
        continueDisabled={
          checkout.step === "userform" &&
          isUserFormPaged &&
          !userFormNav.isLast
            ? !userFormNav.canAdvance
            : !checkout.canContinue()
        }
        isEmbedMode={isEmbedMode}
        validationMessage={
          !checkout.canContinue()
            ? checkout.step === "dates"
              ? isMultiDateEnabled
                ? "Please select at least one date to continue"
                : "Please select a date to continue"
              : checkout.step === "tickets"
                ? checkout.hasMixedPolicies
                  ? "Selected tickets have different cancellation policies"
                  : "Please select at least one ticket"
                : undefined
            : undefined
        }
      />
    </ToastProvider>
  );
}
